import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sprout, Search, Compass } from "lucide-react";

const KIDS_1 =
  "https://images.unsplash.com/photo-1527213315950-eb5bc3403154?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMGxlYXJuaW5nJTIwbmF0dXJlJTIwc3VubGlnaHR8ZW58MHx8fHwxNzg0MjI4ODY2fDA&ixlib=rb-4.1.0&q=85";
const KIDS_2 =
  "https://images.unsplash.com/photo-1533222535026-754c501569dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxraWRzJTIwaW50ZXJhY3RpbmclMjB3b3Jrc2hvcHxlbnwwfHx8fDE3ODQyMjg4Njd8MA&ixlib=rb-4.1.0&q=85";
const KIDS_3 =
  "https://images.unsplash.com/flagged/photo-1567116681178-c326fa4e2c8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxraWRzJTIwaW50ZXJhY3RpbmclMjB3b3Jrc2hvcHxlbnwwfHx8fDE3ODQyMjg4Njd8MA&ixlib=rb-4.1.0&q=85";

const batches = [
  {
    id: "sprouts",
    Icon: Sprout,
    color: "var(--mint)",
    band: "Ages 5 – 8",
    name: "Sprouts",
    question: "Am I safe and loved?",
    focus: "Emotional naming · body safety · self-trust",
    methods: ["Play", "Stories", "Art", "Movement"],
    happening: "At this age, feelings are BIG but words for them are small.",
    outcome:
      "By the end of the year: your child can name what they feel, say no with confidence, and trust their own inner voice.",
    image: KIDS_1,
  },
  {
    id: "explorers",
    Icon: Search,
    color: "var(--violet)",
    band: "Ages 9 – 12",
    name: "Explorers",
    question: "Do I belong and matter?",
    focus: "Self-worth beyond grades · navigating peer pressure",
    methods: ["Activities", "Discussion", "Reflection"],
    happening: "This is the age of comparison. Self-worth starts chasing grades, friends, and approval.",
    outcome:
      "By the end of the year: your child knows who they are beyond their report card — and makes decisions from within.",
    image: KIDS_2,
  },
  {
    id: "navigators",
    Icon: Compass,
    color: "var(--brand)",
    band: "Ages 13 – 18",
    name: "Navigators",
    question: "Who am I, really?",
    focus: "Identity anchoring · inner authority · purpose",
    methods: ["Conversation", "Journaling", "Real Scenarios"],
    happening:
      "The pull is loudest now — social media, relationships, AI, peer pressure, expectations.",
    outcome:
      "By the end of the year: your teen has a clear sense of who they are, what they stand for, and where they are going.",
    image: KIDS_3,
  },
];

export default function Batches() {
  const [active, setActive] = useState(batches[0].id);
  const current = batches.find((b) => b.id === active);
  const CurIcon = current.Icon;

  return (
    <section id="batches" className="paper-bg py-24 md:py-40 relative overflow-hidden" data-testid="compass-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <span className="font-mono-editorial text-[11px] text-[color:var(--muted)]">Three Batches, One Journey</span>
            <h2 className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
              Meeting your child<br />
              <span className="italic font-serif-editorial" style={{ color: "var(--brand)" }}>
                exactly where they are.
              </span>
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 text-[color:var(--ink)]/75 leading-relaxed self-end">
            One session every fifteen days, in-person. Seventy-five focused minutes. A curriculum shaped by developmental psychology and rooted in wisdom traditions.
          </p>
        </div>

        {/* Batch tabs */}
        <div className="flex flex-wrap gap-3 mb-10 md:mb-14" role="tablist" data-testid="compass-tabs">
          {batches.map((b) => {
            const on = active === b.id;
            const Icon = b.Icon;
            return (
              <button
                key={b.id}
                onClick={() => setActive(b.id)}
                role="tab"
                aria-selected={on}
                className={`h-14 md:h-16 px-5 md:px-7 rounded-full border-2 flex items-center gap-3 transition-all duration-500 ${
                  on ? "text-white -translate-y-1" : "text-[color:var(--ink)] hover:-translate-y-0.5"
                }`}
                style={{
                  background: on ? b.color : "transparent",
                  borderColor: on ? b.color : "var(--ink)",
                }}
                data-testid={`compass-tab-${b.id}`}
              >
                <Icon size={18} strokeWidth={1.75} />
                <span className="font-mono-editorial text-[10px] tracking-[0.28em] opacity-80">{b.band}</span>
                <span className="font-display italic text-xl md:text-2xl">{b.name}</span>
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-12 gap-6"
            data-testid={`compass-panel-${current.id}`}
          >
            <div className="col-span-12 lg:col-span-5">
              <div className="relative overflow-hidden border-2 aspect-[4/5] spotlight" style={{ borderColor: "var(--ink)" }}>
                <img src={current.image} alt="" className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 h-14 w-14 rounded-full flex items-center justify-center" style={{ background: current.color }}>
                  <CurIcon size={22} color="var(--ink)" strokeWidth={1.75} />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-end text-[11px] font-mono-editorial text-[color:var(--muted)]">
                <span>60 min · every 15 days · in-person</span>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <span className="font-mono-editorial text-[11px]" style={{ color: current.color }}>
                {current.band}
              </span>
              <h3 className="mt-3 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
                {current.name}
              </h3>

              {/* Signature quote */}
              <p className="mt-6 font-serif-editorial italic text-3xl md:text-4xl leading-[1.15] text-[color:var(--ink)]/80 max-w-[22ch]">
                &ldquo;{current.question}&rdquo;
              </p>

              {/* Methods chips */}
              <div className="mt-8 flex flex-wrap gap-2">
                {current.methods.map((m) => (
                  <span
                    key={m}
                    className="text-xs font-mono-editorial tracking-[0.22em] px-4 py-2 rounded-full border-2"
                    style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
                  >
                    {m}
                  </span>
                ))}
              </div>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-mono-editorial text-[11px] text-[color:var(--muted)] mb-3">— What is happening</h4>
                  <p className="text-[color:var(--ink)]/85 leading-relaxed">{current.happening}</p>
                </div>
                <div>
                  <h4 className="font-mono-editorial text-[11px] text-[color:var(--muted)] mb-3">— What Inner Compass builds</h4>
                  <p className="text-[color:var(--ink)]/85 leading-relaxed">{current.outcome}</p>
                </div>
              </div>

              <a
                href="#enroll"
                className="mt-10 inline-flex items-center justify-between h-14 px-6 rounded-full text-white transition-all duration-500 hover:-translate-y-1"
                style={{ background: "var(--ink)" }}
                data-testid={`compass-enroll-${current.id}`}
              >
                <span className="text-sm tracking-wide">Enroll into Inner Compass</span>
                <span className="ml-4">→</span>
              </a>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
